/* ============================================================
   PORTFOLIO DATA — THE 7 BEAST PROJECTS
   Source of truth for BeastGrid and Axiom Digital sections.
   Data from Anbuselvan T.'s (@Anbu-2006) professional portfolio.
   ============================================================ */

export type ProjectStatus = "LIVE" | "WIP" | "ARCHIVED";

export interface BeastProject {
  id: string;
  index: string;           // "01" … "07" for display
  title: string;
  category: string;
  status: ProjectStatus;
  tagline: string;         // short description
  tech: string[];
  complexityHighlight: string;
  architecture: string;
  repoUrl: string;
  isAxiom?: boolean;       // flag to span 2 columns and switch accent
}

export const beastProjects: BeastProject[] = [
  {
    id: "insightdesk-ai",
    index: "01",
    title: "INSIGHTDESK AI",
    category: "AI Quality Orchestration & Agentic Support",
    status: "WIP",
    tagline:
      "A sophisticated quality orchestration platform that automates troubleshooting and QA for voice and support agents — transforming passive support into active, agentic ecosystems.",
    tech: ["Python", "FastAPI", "LangChain", "OpenAI Whisper", "ChromaDB", "RAG"],
    complexityHighlight:
      "Architecting the high-concurrency backend to handle real-time voice evaluation while maintaining strict consistency in RAG-based troubleshooting logic.",
    architecture:
      "Microservices-oriented. Voice flows through a Whisper STT layer, LangChain agents generate troubleshooting steps, and ChromaDB vector space scores agent responses against dynamic success metrics.",
    repoUrl: "https://github.com/Vanguard-Neural-Systems/InsightDesk_AI",
  },
  {
    id: "healer",
    index: "02",
    title: "HEALER",
    category: "Android Edge AI Safety System",
    status: "LIVE",
    tagline:
      "On-device road accident detection for high-confidence emergency alerting. Operates entirely without cloud dependence — zero latency, remote-ready.",
    tech: ["Kotlin", "Android SDK", "TensorFlow Lite", "Sensor APIs", "Firebase"],
    complexityHighlight:
      "Solving the sensitivity-reliability tradeoff by requiring impact as a mandatory anchor and using activity-aware suppression to prevent false positives.",
    architecture:
      "Multi-sensor physics engine fuses 9+ streams into a 48-feature vector scored by a TFLite classifier in real time. A DetectionCoordinator manages snapshots in thread-safe buffers.",
    repoUrl: "https://github.com/Anbu-2006/HEALER-APP-DOCS",
  },
  {
    id: "jarvis",
    index: "03",
    title: "JARVIS",
    category: "Desktop AI & System Orchestration",
    status: "WIP",
    tagline:
      "A voice-first AI assistant for Windows that bridges conversational intelligence and direct system action — hands-free control of developer workflows and OS-level tasks.",
    tech: ["Python", "FastAPI", "WebSockets", "NVIDIA NIM · Llama 3", "Edge-TTS", "Playwright"],
    complexityHighlight:
      "Maintaining a low-latency, real-time orchestration loop between speech generation and non-blocking system automation over persistent WebSocket sessions.",
    architecture:
      "Full-duplex loop: voice → LLM router → action-tag workflows ([ACTION:OPEN_VSCODE]). Edge-TTS streams audio back while side-effects execute deterministically.",
    repoUrl: "https://github.com/Anbu-2006/Jarvis-assitant",
  },
  {
    id: "carbontrack",
    index: "04",
    title: "CARBONTRACK",
    category: "Android FinTech & Sustainability",
    status: "LIVE",
    tagline:
      "Privacy-first carbon accounting that gamifies sustainable mobility. Auto-classifies transport modes to calculate environmental impact and reward behavior change.",
    tech: ["Jetpack Compose", "Kotlin", "Room", "Hilt", "WorkManager", "TFLite"],
    complexityHighlight:
      "Implementing Geospatial Anonymization to protect user location privacy while ensuring anti-fraud trip plausibility for rewards.",
    architecture:
      "Offline-first. On-device TFLite classifies transport modes. Use-cases anonymize location to zone hashes and sanitize timestamps before Room persistence; cloud sync is constraint-gated.",
    repoUrl: "https://github.com/Anbu-2006/Carbon_Track_App",
  },
  {
    id: "aegis",
    index: "05",
    title: "AEGIS",
    category: "Cyber Security / Social Engineering Defense",
    status: "WIP",
    tagline:
      "A cognitive scam-defense platform detecting social-engineering threats across live voice, text, and uploaded documents — a digital firewall against AI-driven fraud.",
    tech: ["Python", "FastAPI", "PostgreSQL", "SQLAlchemy Async", "Gemini APIs", "Flutter"],
    complexityHighlight:
      "Decoupling database persistence from inference in real-time streaming pipelines so the security scan never adds latency to communication.",
    architecture:
      "Multimodal pipelines run parallel intent scoring and deepfake detection. Heavy AI inference is decoupled from the response path via FastAPI BackgroundTasks and asyncio.create_task over WebSockets.",
    repoUrl: "https://github.com/Anbu-Navin-Devs/Aegis-Scam-Firewall",
  },
  {
    id: "argofloat-chart",
    index: "06",
    title: "ARGOFLOAT CHART",
    category: "Data Engineering & Open Intelligence",
    status: "LIVE",
    tagline:
      "An open intelligence layer providing natural-language access to global ARGO oceanographic observations. Query complex datasets in plain English.",
    tech: ["Python", "Flask", "pandas", "LangChain", "Groq · OpenAI", "CockroachDB"],
    complexityHighlight:
      "Engineering a safe AI-to-SQL execution engine that enforces read-only contracts through allowlisted statements and anti-injection guards.",
    architecture:
      "Versioned NL endpoints route through an AI 'brain' generating SQL. Queries pass a multi-layered sanitization engine before execution; results cache and surface via an MCP-style tool manifest.",
    repoUrl: "https://github.com/Anbu-Navin-Devs/ARGOFLOAT-CHART",
  },
];

/* ============================================================
   AXIOM DIGITAL — BUSINESS IMPACT CARD
   Spans two columns in the Beast Grid.
   ============================================================ */

export interface AxiomBentoData {
  id: string;
  index: string;
  title: string;
  category: string;
  status: ProjectStatus;
  tagline: string;
  tech: string[];
  complexityHighlight: string;
  architecture: string;
  repoUrl: string;
  metrics: { value: string; label: string }[];
  services: { name: string; note: string }[];
  isAxiom: true;
}

export const axiomDigital: AxiomBentoData = {
  id: "axiom-digital",
  index: "07",
  title: "AXIOM DIGITAL WEB SUITE",
  category: "Full-Stack Web Design & Brand Growth",
  status: "LIVE",
  tagline:
    "The digital presence arm of the Anbu-2006 portfolio. Axiom bridges complex engineering with market-ready commercial interfaces — transforming brand identities into high-conversion web ecosystems.",
  tech: ["Next.js", "Vite", "TypeScript", "Tailwind CSS", "GSAP", "Three.js"],
  complexityHighlight:
    "Designing high-end, vibrant interfaces that maintain near-perfect Lighthouse scores while supporting 3D interactive assets and heavy motion graphics.",
  architecture:
    "SEO-optimized architectures with custom design systems. Prioritizes retention and conversion — bridging technical backends with commercial frontends through component lazy-loading and advanced image optimization.",
  repoUrl: "https://github.com/Axiom-Digital-Studios",
  metrics: [
    { value: "95–100", label: "LIGHTHOUSE SCORES" },
    { value: "−40%",   label: "ASSET LOAD TIME" },
    { value: "100%",   label: "MOBILE FLUIDNESS" },
    { value: "5+",     label: "BRANDS SHIPPED" },
  ],
  services: [
    { name: "High-Performance Web Design", note: "Next.js · Tailwind · Three.js showcases" },
    { name: "Integrated Content Strategy", note: "Visual-first storytelling · cinematic brand presentation" },
  ],
  isAxiom: true,
};

export type BentoItem = BeastProject | AxiomBentoData;

/* Grid render order: 6 Beast projects + Axiom (final, spans 2 cols) */
export const bentoItems: BentoItem[] = [
  ...beastProjects,
  axiomDigital,
];
